import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Star, Briefcase, TrendingUp, GraduationCap, Palette, Smartphone, Sparkles } from 'lucide-react';
import Section from '../components/common/Section';
import Button from '../components/common/Button';

const stats = [
  { icon: Users, value: '1,200+', label: 'Learners' },
  { icon: Star, value: '4.8/5', label: 'Average Rating' },
  { icon: Briefcase, value: '10+', label: 'Hiring Partners' },
  { icon: TrendingUp, value: '90%', label: 'Placement Rate' },
];

const courses = [
  {
    title: 'UI/UX Design with AI',
    duration: '12 weeks',
    badge: 'Beginner-Friendly',
    icon: Palette,
    description: 'Master modern design principles, Figma workflows, and AI-assisted UI/UX — from research to polished, job-ready case studies.',
    link: '/courses/ui-ux-design-with-ai',
  },
  {
    title: 'App Development with AI',
    duration: '14 weeks',
    badge: 'No Coding Required',
    icon: Smartphone,
    description: 'Build and ship real Android/iOS/cross-platform apps using Flutter, React Native, and AI coding assistants — no prior coding experience needed.',
    link: '/courses/app-development-with-ai',
  },
];

const benefits = [
  {
    icon: Users,
    title: 'Industry Mentors',
    description: 'Learn directly from professionals who ship client projects every week — not just instructors who teach theory.',
  },
  {
    icon: Sparkles,
    title: 'Live Client Projects',
    description: 'Work on real-world capstone projects modeled after actual Pyrus Media client briefs.',
  },
  {
    icon: GraduationCap,
    title: 'AI-First Curriculum',
    description: 'Every module integrates AI tools where they genuinely save time — not as a gimmick, but as a core part of the workflow.',
  },
  {
    icon: TrendingUp,
    title: 'Placement Support',
    description: 'Portfolio reviews, interview prep, and direct consideration for freelance and intern roles at Pyrus Media.',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: 'easeOut' },
  }),
};

const Academy = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": "https://pyrusmedia.in/academy#academy",
    "url": "https://pyrusmedia.in/academy",
    "name": "PyrusMedia Academy",
    "description": "PyrusMedia Academy provides learning and training resources related to digital marketing, technology, and digital solutions.",
    "isPartOf": {
      "@id": "https://pyrusmedia.in/#website"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PyrusMedia",
      "url": "https://pyrusmedia.in/"
    }
  };

  return (
    <>
      <Helmet>
        <title>PyrusMedia Academy — Learn UI/UX & App Development with AI</title>
        <meta name="description" content="Join PyrusMedia Academy and learn UI/UX design and app development with AI-powered tools. Industry mentors, live projects, and placement support." />
        <link rel="canonical" href="https://pyrusmedia.in/academy" />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="pt-32 pb-20 bg-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#bebd19]/20 rounded-full blur-[100px] pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 bg-[#bebd19]/10 text-[#bebd19] rounded-full mb-6 border border-[#bebd19]/20">
              <Sparkles className="w-4 h-4" />
              AI-Powered Learning
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight"
          >
            PyrusMedia Academy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-500 max-w-3xl mx-auto mb-10"
          >
            Learn UI/UX & App Development with AI — practical, project-first courses mentored by working professionals.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <Button to="/courses/ui-ux-design-with-ai" className="px-8 py-4 text-base">
              UI/UX Design Course
            </Button>
            <Button to="/courses/app-development-with-ai" variant="outline" className="px-8 py-4 text-base">
              App Development Course
            </Button>
          </motion.div>
        </div>
      </section>

      {/* STATS STRIP */}
      <Section className="bg-gray-50 py-12 border-y border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="text-center"
            >
              <stat.icon className="w-8 h-8 text-[#bebd19] mx-auto mb-3" />
              <div className="text-3xl md:text-4xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* COURSE CARDS */}
      <Section className="bg-white py-20">
        <div className="text-center mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            Choose Your Path
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-gray-500 text-lg max-w-2xl mx-auto"
          >
            Two intensive, cohort-based programs designed to take you from beginner to job-ready — powered by AI.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {courses.map((course, i) => (
            <motion.div
              key={course.title}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <Link
                to={course.link}
                className="group block bg-gray-50 border border-gray-200 rounded-2xl p-8 hover:border-[#bebd19] hover:shadow-lg hover:shadow-[#bebd19]/10 transition-all duration-300 h-full"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 bg-[#bebd19]/10 rounded-xl flex items-center justify-center">
                    <course.icon className="w-6 h-6 text-[#bebd19]" />
                  </div>
                  <span className="text-xs font-bold px-3 py-1 bg-gray-100 text-gray-600 rounded-full border border-gray-200">
                    {course.badge}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-[#bebd19] transition-colors">
                  {course.title}
                </h3>
                <p className="text-sm text-gray-500 mb-4">{course.duration}</p>
                <p className="text-gray-500 leading-relaxed mb-6">
                  {course.description}
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#bebd19] group-hover:gap-3 transition-all">
                  View Course Details
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* WHY LEARN FROM US */}
      <Section className="bg-gray-50 py-20">
        <div className="text-center mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            Why Learn From Us
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-gray-500 text-lg max-w-2xl mx-auto"
          >
            We don't just teach — we build. Learn from a team that ships real digital products every week.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {benefits.map((benefit, i) => (
            <motion.div
              key={benefit.title}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="bg-white p-6 rounded-2xl border border-gray-200 hover:border-[#bebd19]/40 transition-colors"
            >
              <benefit.icon className="w-10 h-10 text-[#bebd19] mb-4" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* CTA SECTION */}
      <Section className="bg-white py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-[#bebd19]/5 blur-[120px] rounded-full pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10 text-center max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Start Your Journey Today
            </h2>
            <p className="text-xl text-gray-500 mb-10 max-w-2xl mx-auto">
              Book a free masterclass to experience the teaching style, or jump straight into a batch. Limited seats per cohort.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button to="/free-masterclass" className="px-8 py-4 text-base">
                Attend Free Masterclass
              </Button>
              <Button to="/pricing-batches" variant="outline" className="px-8 py-4 text-base">
                View Batches & Pricing
              </Button>
            </div>
          </motion.div>
        </div>
      </Section>
    </>
  );
};

export default Academy;
